import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AuthInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  /** Label shown above the input. Required for a11y. */
  label?: string;
  /** Screen-reader-only label, used when a visible label would crowd the UI. */
  srLabel?: string;
  /** Helper text shown below the input (greyed). */
  helper?: string;
  /** Error message — when present, the input gets aria-invalid + red border. */
  error?: string;
  /** Optional leading icon inside the input (e.g. Lock for password fields). */
  leadingIcon?: ReactNode;
  /** When true, renders a show/hide-password toggle on the right side. */
  withPasswordToggle?: boolean;
  /** Optional inline action on the same row as the label (e.g. "Forgot password?"). */
  labelAction?: ReactNode;
}

/**
 * AuthInput — labeled input that composes the dashboard's `.adp-input`
 * class (radius-button, branded focus ring) plus a mono eyebrow label
 * and helper/error association.
 *
 * a11y:
 * - Visible label via `<label htmlFor>` paired with `useId`.
 * - `aria-invalid` set only when `error` is non-empty (after-submit timing).
 * - `aria-describedby` chains helper + error ids.
 * - Show/hide-password toggle is a real button with aria-label + aria-pressed.
 */
export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(function AuthInput(
  {
    label,
    srLabel,
    helper,
    error,
    leadingIcon,
    withPasswordToggle = false,
    labelAction,
    type = "text",
    className = "",
    style,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation();
  const generatedId = useId();
  const id = (rest.name ? `${rest.name}-${generatedId}` : generatedId).replace(/[:]/g, "-");
  const helperId = helper ? `${id}-helper` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined;

  const [revealed, setRevealed] = useState(false);
  const effectiveType = withPasswordToggle && revealed ? "text" : type;

  const padLeft = leadingIcon ? 38 : 12;
  // 44px right-padding reserves room for the password-toggle button
  // (min-w-[44px] for WCAG 2.5.8 + browser-extension overlap defense).
  const padRight = withPasswordToggle ? 44 : 12;

  return (
    <div>
      {(label || labelAction) && (
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
          {label && (
            <label htmlFor={id} className="auth-label" style={{ marginBottom: 0 }}>
              {label}
            </label>
          )}
          {labelAction && <div className="flex-shrink-0">{labelAction}</div>}
        </div>
      )}
      {!label && srLabel && (
        <label htmlFor={id} className="sr-only">{srLabel}</label>
      )}

      <div className="relative">
        {leadingIcon && (
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--muted-foreground)] pointer-events-none"
          >
            {leadingIcon}
          </span>
        )}

        <input
          {...rest}
          id={id}
          ref={ref}
          type={effectiveType}
          className={`auth-input-styled ${className}`.trim()}
          style={{ paddingLeft: padLeft, paddingRight: padRight, ...style }}
          aria-invalid={error ? "true" : rest["aria-invalid"]}
          aria-describedby={[describedBy, rest["aria-describedby"]].filter(Boolean).join(" ") || undefined}
        />

        {withPasswordToggle && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center justify-center min-w-[44px] px-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--brand)] focus-visible:outline-offset-2 transition-colors motion-reduce:transition-none"
            aria-label={revealed ? t("password.hidePassword") : t("password.showPassword")}
            aria-pressed={revealed}
            tabIndex={rest.disabled ? -1 : 0}
            disabled={rest.disabled}
          >
            {revealed ? (
              <EyeOff className="w-4 h-4" aria-hidden="true" />
            ) : (
              <Eye className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        )}
      </div>

      {helper && !error && (
        <p id={helperId} className="auth-helper">
          {helper}
        </p>
      )}
      {error && (
        <p id={errorId} className="auth-helper auth-helper-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
